import React from 'react';

const iframeStyle = {
  width: '100%',
  height: '100%',
  border: 0,
} as const;

/**
 * Returns the 11-char YouTube video ID for any of the common URL forms,
 * or null if the URL isn't a recognisable YouTube link.
 */
export function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

type Props = {
  videoId: string;
  className?: string;
};

/**
 * Renders a YouTube video via a same-origin static proxy page
 * (`/embed/youtube.html?v=...`) instead of pointing the iframe directly at
 * youtube.com. YouTube blocks embeds with a missing/invalid Referer (error
 * 153) — Tauri's `tauri://localhost` on iOS/macOS can't supply one. Routing
 * through a proxy hosted on a real http(s) origin gives YouTube a valid
 * Referer regardless of platform.
 */
export const YouTubeProxyEmbed: React.FC<Props> = ({ videoId, className }) => {
  const embedOrigin =
    import.meta.env.VITE_NX_WEBAPP_ORIGIN ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  return (
    <iframe
      title="YouTube video"
      src={`${embedOrigin}/embed/youtube.html?v=${videoId}`}
      style={iframeStyle}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      className={className}
    />
  );
};
